import { z } from 'zod';
/**
 * The message envelope. See ADR-0004.
 *
 * The server stores `payload` and NEVER parses it. Everything the server has to
 * index lives in its own field. This is what makes end-to-end encryption a
 * client-only change later: phase 2 flips `alg` to `xchacha20poly1305` and the
 * server keeps working untouched.
 *
 * Do not add a field here that the server needs to read but that would be
 * encrypted in phase 2. That is the one mistake this design exists to prevent.
 */
/** ULID: sortable by creation time, generated client-side so retries are idempotent. */
export const IdSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'expected a ULID');
export const AlgSchema = z.enum(['plain', 'xchacha20poly1305']);
export const EnvelopeSchema = z.object({
    v: z.literal(1),
    id: IdSchema,
    convId: IdSchema,
    senderId: IdSchema,
    ts: z.number().int().positive(),
    alg: AlgSchema,
    /** Base64. Opaque to the server in every phase. */
    payload: z.string().max(64 * 1024),
    replyTo: IdSchema.optional(),
    /** Media ids referenced by the payload. Server-visible so it can enforce quotas. */
    mediaIds: z.array(IdSchema).max(10).optional(),
});
/**
 * The decoded shape of `payload` while `alg` is "plain".
 * Lives on the client only. The server must never import this to parse a payload.
 */
export const MessageBodySchema = z.object({
    kind: z.enum(['text', 'image', 'video', 'audio', 'system']),
    text: z.string().max(8000).optional(),
    media: z
        .array(z.object({
        id: IdSchema,
        mime: z.string().max(100),
        bytes: z.number().int().nonnegative(),
        width: z.number().int().positive().optional(),
        height: z.number().int().positive().optional(),
        durationMs: z.number().int().positive().optional(),
        /** Tiny inline preview so history stays readable after media expires (ADR-0006). */
        thumb: z.string().max(32 * 1024).optional(),
    }))
        .max(10)
        .optional(),
});
//# sourceMappingURL=envelope.js.map